import type { ReactNode } from "react";
import { RevealOnScroll } from "./RevealOnScroll";

type Props = {
  number?: string;
  kicker?: string;
  headline?: ReactNode;
  caption?: string;
  variant?: "default" | "tinted" | "deep" | "compact";
  children: ReactNode;
  id?: string;
};

export function Section({
  number,
  kicker,
  headline,
  caption,
  variant = "default",
  children,
  id,
}: Props) {
  const bg =
    variant === "tinted"
      ? "bg-bg-subtle text-ink"
      : variant === "deep"
      ? "bg-bg-deep text-white"
      : "bg-white text-ink";
  const numColor = variant === "deep" ? "text-white/75" : "text-ink-4";
  const captionColor = variant === "deep" ? "text-white/90" : "text-ink-3";
  const isDeep = variant === "deep";

  return (
    <section id={id} className={`${bg} ${variant === "compact" ? "py-20" : "py-28"}`}>
      <div className="mx-auto grid max-w-[1200px] grid-cols-1 gap-6 px-6 md:grid-cols-[64px_minmax(0,1fr)] md:gap-10 md:px-12">
        {number ? (
          <div
            aria-hidden
            className={`font-mono text-xs tracking-wider leading-[1.35] ${numColor}`}
          >
            {number}
          </div>
        ) : (
          <div aria-hidden className="hidden md:block" />
        )}
        <div className="min-w-0">
          {(kicker || headline || caption) && (
            <header className="mb-12 max-w-3xl">
              {kicker && (
                <RevealOnScroll>
                  <p className={`eyebrow mb-5 ${isDeep ? "!text-white/85" : ""}`}>{kicker}</p>
                </RevealOnScroll>
              )}
              {headline && (
                <RevealOnScroll>
                  <h2 className="text-section font-display editorial-italic">
                    {headline}
                  </h2>
                </RevealOnScroll>
              )}
              {caption && (
                <RevealOnScroll delay={120}>
                  <p className={`mt-6 text-lg leading-relaxed ${captionColor}`}>
                    {caption}
                  </p>
                </RevealOnScroll>
              )}
            </header>
          )}
          {children}
        </div>
      </div>
    </section>
  );
}
